import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

export type ContextMenuItem =
  | { type: "action"; label: string; icon: React.ReactNode; onClick: () => void; variant?: "destructive" }
  | { type: "separator" };

interface Props {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

export default function TaskContextMenu({ x, y, items, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Delay to prevent the triggering right-click from immediately closing
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
      document.addEventListener("contextmenu", onMouseDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("contextmenu", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  // Adjust position to stay within viewport
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    if (rect.right > window.innerWidth - pad) {
      el.style.left = `${Math.max(pad, x - rect.width)}px`;
    }
    if (rect.bottom > window.innerHeight - pad) {
      el.style.top = `${Math.max(pad, y - rect.height)}px`;
    }
  }, [x, y]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      className="fixed z-[9999] min-w-[180px] bg-surface border border-border rounded-lg shadow-raised py-1 animate-in fade-in-0 zoom-in-95 origin-top-left duration-100"
      style={{ left: x, top: y }}
    >
      {items.map((item, i) => {
        if (item.type === "separator") {
          return <div key={`sep-${i}`} className="my-1 h-px bg-border" />;
        }
        return (
          <button
            key={`item-${i}`}
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              item.onClick();
              onClose();
            }}
            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-[13px] text-left transition-colors ${
              item.variant === "destructive"
                ? "text-destructive hover:bg-destructive/10"
                : "text-foreground hover:bg-muted"
            }`}
          >
            <span className="flex-shrink-0 w-4 flex items-center justify-center">
              {item.icon}
            </span>
            {item.label}
          </button>
        );
      })}
    </div>,
    document.body
  );
}
